import "./SearchBox.css";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useForm, Controller } from "react-hook-form";
import SearchBoxCalendarDate from "./SearchBoxCalendarDate";
import useFocusableExternalSerachScreenElements from "./useFocusableExternalSerachScreenElements";
import { useLoader } from "../../hooks/LoaderContext";
import { useModal } from "../../hooks/ModalContext";

const isDisableStatusDropdown = (
  isModalVisible = false,
  statusDropdownFieldName = "",
  name = ""
) => {
  return (
    (isModalVisible || window?.self?.name === "external-lookup-iframe") &&
    statusDropdownFieldName === name
  );
};

const getDisabledDropdownProps = (disabledDropdown, name = '', isModalVisible = false, statusDropdownFieldName = "") => {
  let disabledProps = {}
  if (typeof disabledDropdown === 'boolean' && disabledDropdown) {
    disabledProps = {
      disabled: true
    }
  }
   
  if ((Object.keys(disabledDropdown).length && disabledDropdown?.[name]) || isDisableStatusDropdown(isModalVisible, statusDropdownFieldName, name)) {
    disabledProps = {
      dropdownIcon: <></>,
      disabled: true,
      pt: {
        root: { style: { opacity: 1, border: "none", background: "transparent" } },
        input: { style: { paddingLeft: 1 } },
        trigger: {
          style: {
            display: "none",
          },
        },
      },
    };
  }

  return disabledProps;
}

const SearchBox = ({
  formFields,
  dropdownValues,
  paramsData,
  updataParamData,
  disabledDropdown = false,
  disabledFields = {},
  useNewLayout = false,
  firstRowCount = null, 
  secondRowCount = null,
}) => {
 
  const { register, control, handleSubmit, setValue, reset } = useForm({
    defaultValues: paramsData,
  });

  const { isLoaderEnabled } = useLoader();

   const { isModalVisible } = useModal();

  useFocusableExternalSerachScreenElements();

  const [focusedInput, setFocusedInput] = useState(null);

  const inputRefs = useRef([]);

  const onSubmit = (data) => {
    console.log(data)
    const updatedObj = { ...data };
    for (let key in updatedObj) {
      if (updatedObj[key] === undefined) {
        updatedObj[key] = "";
      }
    }
    data = updatedObj;
    updataParamData(data);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const handleFocus = (inputName) => {
    setFocusedInput(inputName);
  };

  const getDate = (dateString) => (dateString ? new Date(dateString) : null);

  const getFieldLabel = (fieldValue, options) => {
    
    if(options!==undefined){
      const filteredValues = options.filter(({ value }) => {
        return value === fieldValue;
      });
      return filteredValues.length ? filteredValues[0].label : "";
    }};

  useEffect(() => {
// Set default values when paramsData changes
    if (paramsData) {
      reset(paramsData);
    }
  }, [paramsData, reset]);

  useEffect(() => {
    if (!isLoaderEnabled) {
      const timeout = setTimeout(() => {
        const firstInput = inputRefs?.current?.filter(Boolean)?.[0];
				if (firstInput) {
					firstInput.focus();
					firstInput.select?.();
				}
      }, 100);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isLoaderEnabled]);

  const statusDropdownFieldName = useMemo(
    () => formFields?.find((field) => field?.label === "Status")?.name || "",
    [formFields]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key } = event;
      if (statusDropdownFieldName && key === "F8") {
        event.preventDefault();
        const targetDropdown = inputRefs.current?.find(
          (item) => item?.props?.id === statusDropdownFieldName
        );
        targetDropdown?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);


  return (
    <>
      <div className="search_box_container">
        <form className="search_form" aria-autocomplete="none" autoComplete="off">
          {useNewLayout ? (
            <>
              <div className="row">
                {formFields.slice(0, firstRowCount).map((formField, i) => {
                  const { name, type, label, maxLength, fieldLabel, fieldNature } = formField;
                  return (
                    <div className="col col-20 no-label" key={formField.name}>
                      {type === "text" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{field.value}</span>;
                              } else {
                                const { ref, ...rest } = register(name);
                                return (
                                  <InputText
                                    id={name}
                                    {...rest}
                                    onChange={(e) => {
                                      setValue(e.target.name, e.target.value.toUpperCase());
                                    }}
                                    maxLength={maxLength}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => handleFocus(name)}
                                    ref={(el) => {
                                      ref(el);
                                      inputRefs.current[i] = el;
                                    }}
                                    className={focusedInput === name ? "p-inputtext-highlight" : ""}
                                    style={{ textTransform: "uppercase" }}
                                    disabled={disabledFields?.[name]}
                                    autoComplete="off"
                                    aria-autocomplete="none"
                                  />
                                );
                              }
                            }}
                          />
                        </>
                      ) : type === "dropdown" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{getFieldLabel(field.value, dropdownValues[fieldLabel])}</span>;
                              } else {
                                return (
                                  <Dropdown
                                    id={name}
                                    value={field.value}
                                    options={dropdownValues[fieldLabel] || []}
                                    optionLabel="label"
                                    placeholder={label}
                                    {...getDisabledDropdownProps(
                                      disabledDropdown,
                                      name,
                                      isModalVisible,
                                      statusDropdownFieldName
                                    )}
                                    ref={(ref) => {
                                      inputRefs.current[i] = ref;
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e.value);
                                      if (e.value !== field.value) {
                                        handleSubmit(onSubmit)();
                                      }
                                    }}
                                    onFocus={() => handleFocus(name)}
                                    className={
                                      focusedInput === name
                                        ? "p-dropdown-highlight"
                                        : ""
                                    }
                                  />
                                );
                              }
                            }}
                          />
                        </>
                      ) : type === "date" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{field.value}</span>;
                              } else {
                              return (
                                <SearchBoxCalendarDate
                                  name={name}
                                  getRef={(el) => {
                                    inputRefs.current[i] = el;
                                  }}
                                  onChange={field.onChange}
                                  handleSubmit={handleSubmit(onSubmit)}
                                  defaultValue={paramsData?.[name] || ""}
                                />
                              );
                              }
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="row">
                {formFields.slice(firstRowCount, firstRowCount + secondRowCount).map((formField, i) => {
                  const { name, type, label, maxLength, fieldLabel, fieldNature } = formField;
                  return (
                    <div className="col col-20 no-label" key={formField.name}>
                      {type === "text" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{field.value}</span>;
                              } else {
                                const { ref, ...rest } = register(name);
                                return (
                                  <InputText
                                    id={name}
                                    {...rest}
                                    onChange={(e) => {
                                      setValue(e.target.name, e.target.value.toUpperCase());
                                    }}
                                    maxLength={maxLength}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => handleFocus(name)}
                                    ref={(el) => {
                                      ref(el);
                                      inputRefs.current[i] = el;
                                    }}
                                    className={focusedInput === name ? "p-inputtext-highlight" : ""}
                                    style={{ textTransform: "uppercase" }}
                                    disabled={disabledFields?.[name]}
                                    autoComplete="off"
                                    aria-autocomplete="none"
                                  />
                                );
                              }
                            }}
                          />
                        </>
                      ) : type === "dropdown" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{getFieldLabel(field.value, dropdownValues[fieldLabel])}</span>;
                              } else {
                                return (
                                  <Dropdown
                                    id={name}
                                    value={field.value}
                                    options={dropdownValues[fieldLabel] || []}
                                    optionLabel="label"
                                    placeholder={label}
                                    {...getDisabledDropdownProps(
                                      disabledDropdown,
                                      name,
                                      isModalVisible,
                                      statusDropdownFieldName
                                    )}
                                    ref={(ref) => {
                                      inputRefs.current[i] = ref;
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e.value);
                                      if (e.value !== field.value) {
                                        handleSubmit(onSubmit)();
                                      }
                                    }}
                                    onFocus={() => handleFocus(name)}
                                    className={
                                      focusedInput === name
                                        ? "p-dropdown-highlight"
                                        : ""
                                    }
                                  />
                                );
                              }
                            }}
                          />
                        </>
                      ) : type === "date" ? (
                        <>
                          <div className="input_label_container">
                            <label htmlFor={name} className="search_label">
                              {label}
                            </label>
                          </div>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => {
                              if (fieldNature === "readOnly") {
                                return <span id={name}>{field.value}</span>;
                              } else {
                                return (
                                  <SearchBoxCalendarDate
                                    name={name}
                                    getRef={(el) => {
                                      inputRefs.current[i] = el;
                                    }}
                                    onChange={field.onChange}
                                    handleSubmit={handleSubmit(onSubmit)}
                                    defaultValue={paramsData?.[name] || ''}
                                  />
                                );
                              }
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="row">
              {formFields.map((formField, i) => {
                const { name, type, label, maxLength, fieldLabel, fieldNature } = formField;
                return (
                  <div className="col col-20 no-label" key={formField.name}>

                    {type === "text" ? (
                      <>

                        <div className="input_label_container">
                          <label htmlFor={name} className="search_label">
                            {label}
                          </label>
                        </div>

                        <Controller
                          name={name}
                          control={control}
                          render={({ field }) => {
                            if(fieldNature === "readOnly"){return <span id={name}>{field.value}</span>;
                            }else{
                              const {ref, ...rest} = register(name);
                              return (
                                <InputText
                                  id={name}
                                  {...rest}
                                  onChange={(e) => {
                                    setValue(e.target.name, e.target.value.toUpperCase());
                                  }}
                                  maxLength={maxLength}
                                  onKeyDown={handleKeyDown}
                                  onFocus={() => handleFocus(name)}
                                  ref={(el) => {
                                    ref(el);
                                    inputRefs.current[i] = el;
                                  }}
                                  className={
                          focusedInput === name ? "p-inputtext-highlight" : ""
                        }
                                  style={{ textTransform: "uppercase" }}
                                  disabled={disabledFields?.[name]}
                                  autoComplete="off"
                                  aria-autocomplete="none"
                                />
                              );}
                          }}
                        />
                      </>
                    ) : type === "dropdown" ? (
                      <>
                        <div className="input_label_container">
                          <label htmlFor={name} className="search_label">
                            {label}
                          </label>
                        </div>
                        <Controller
                          name={name}
                          control={control}
                          render={({ field }) => {
                            if(fieldNature === "readOnly"){
                              return(
                            <span id={name}>{getFieldLabel(field.value, dropdownValues[fieldLabel] )}</span>
                        )
                        
                      }else{
                              return(
                                <Dropdown
                                  id={name}
                                  value={field.value}
                                  options={dropdownValues[fieldLabel] || []}
                                  optionLabel="label"
                                  placeholder={label}
                                  {...getDisabledDropdownProps(disabledDropdown, name,  isModalVisible, statusDropdownFieldName)}
                                  ref={(ref) => {
                                    inputRefs.current[i] = ref;
                                  }}
                                  onChange={(e) => {
                                    field.onChange(e.value);
                                    if (e.value !== field.value) {
                                      handleSubmit(onSubmit)();
                                    }
                                  }}
                                  onFocus={() => handleFocus(name)}
                                  className={
                            focusedInput === name ? "p-dropdown-highlight" : ""
                            }
                          />
                  )}}}
                        />
                      </>
                    ) : type==="date"?(
                      <>

                        <div className="input_label_container">
                          <label htmlFor={name} className="search_label">
                            {label}
                          </label>
                        </div>
                        <Controller
                          name={name}
                          control={control}
                          render={({ field }) => {
                            if(fieldNature === "readOnly"){return <span id={name}>{field.value}</span>;
                            }else{
                              return (
                                <SearchBoxCalendarDate
                                  name={name}
                                  getRef={(el) => {
                                    inputRefs.current[i] = el;
                                  }}
                                  onChange={field.onChange}
                                  handleSubmit={handleSubmit(onSubmit)}
                                  defaultValue={paramsData?.[name] || ""}
                                />
                              );}}
                            }
                           />
                      </>
                   ):(<></>)}
                  </div>
                );
              })}
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default SearchBox;